import { Fragment } from 'react'
import { keepPreviousData } from '@tanstack/react-query'

import type { Deal } from '@/api/gen'
import { useDealPersonalizedDealsRetrieve } from '@/api/gen'
import { AbsoluteSuitsOnCorners } from '@/components/absolute-suits-on-corners'
import { DealCard } from '@/components/deal-card'
import { Loader } from '@/components/loader'
import { NoDealsPlaceholder } from '@/components/no-deals-placeholder'
import { SupportBanner } from '@/components/support-banner'
import { DealLegendBanner } from '@/pages/deals/components/deal-legend-banner'
import { DealsPageHeader } from '@/pages/deals/components/deals-page-header'
import { GTOFreeBanner } from '@/pages/deals/components/gto-free-banner'
import { PersonalPropositionBanner } from '@/pages/deals/components/personal-proposition-banner'

export const RootRoute = () => {
  const { data, isFetching } = useDealPersonalizedDealsRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      placeholderData: keepPreviousData,
    },
  })

  if (isFetching) {
    return <Loader />
  }

  if (!data?.deals || (Array.isArray(data.deals) && data.deals.length === 0)) {
    return (
      <div data-tst="deals-page">
        <NoDealsPlaceholder />
      </div>
    )
  }

  return (
    <section data-tst="deals-page">
      <DealsPageHeader location={data.user_location_display} />
      <div className="relative flex flex-col gap-4 px-12 pt-16 md:pt-8 sm:px-4">
        <AbsoluteSuitsOnCorners top={0} />
        <DealLegendBanner variant="exclusive" />
        <DealsListWithBanners deals={data.deals} />
      </div>
      <div className="flex flex-col gap-4">
        <SupportBanner />
        <div className="px-4 sm:px-12">
          <DealLegendBanner variant="updates" />
        </div>
      </div>
    </section>
  )
}

const DealsListWithBanners = ({ deals }: { deals: Deal[] }) => {
  return (
    <>
      {deals.map((deal, index) => {
        const eachSecond = (index + 1) % 2 === 0
        const eachFourth = (index + 1) % 4 === 0

        return (
          <Fragment key={deal.poker_site_slug}>
            <DealCard {...deal} />
            {eachFourth && <GTOFreeBanner />}
            {eachSecond && !eachFourth && <PersonalPropositionBanner />}
          </Fragment>
        )
      })}
      {deals.length < 2 && <PersonalPropositionBanner />}
      {deals.length < 4 && <GTOFreeBanner />}
    </>
  )
}
